import { LocaleData, LocaleConfig } from '../locales';

export class LangModal {
    private backdrop: HTMLDivElement;
    private card: HTMLDivElement;

    constructor(
        private options: {
            onClose: () => void;
            t: (key: keyof LocaleData) => string;
            locale: string;
            supportedLocalesConfig: LocaleConfig[];
            direction?: 'ltr' | 'rtl';
            changeLanguage: (newLang: string) => void;
        }
    ) {
        this.backdrop = document.createElement('div');
        this.backdrop.className = 'modal-backdrop modal-backdrop-top';
        this.backdrop.onclick = (e) => {
            if (e.target === this.backdrop) this.options.onClose();
        };

        this.card = document.createElement('div');
        this.card.className = 'modal-card lang-modal-card';
        if (this.options.direction) {
            this.card.dir = this.options.direction;
        }

        const closeBtn = document.createElement('button');
        closeBtn.className = 'modal-close-x';
        closeBtn.onclick = this.options.onClose;
        closeBtn.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;
        this.card.appendChild(closeBtn);

        const header = document.createElement('h2');
        header.className = 'modal-header';
        header.textContent = this.options.t('select_language');
        this.card.appendChild(header);

        const searchContainer = document.createElement('div');
        searchContainer.className = 'lang-search-container';

        const searchInput = document.createElement('input');
        searchInput.type = 'text';
        searchInput.className = 'lang-search-input';
        searchInput.placeholder = this.options.t('search_placeholder');

        searchContainer.appendChild(searchInput);
        this.card.appendChild(searchContainer);

        const listContainer = document.createElement('div');
        listContainer.className = 'lang-list-container';

        const buttons: { btn: HTMLButtonElement, cfg: LocaleConfig }[] = [];

        // Sort configs alphabetically by English name
        const sortedConfigs = [...this.options.supportedLocalesConfig].sort((a, b) => 
            a.fullName.localeCompare(b.fullName)
        );

        sortedConfigs.forEach(cfg => {
            const btn = document.createElement('button');
            btn.className = `modal-btn lang-btn-item ${this.options.locale === cfg.id ? 'btn-gradient' : 'btn-outline'}`;
            btn.onclick = () => this.options.changeLanguage(cfg.id);

            const nameSpan = document.createElement('span');
            // Show both native name and english name if they differ, or just one
            if (cfg.nativeName.toLowerCase() !== cfg.fullName.toLowerCase()) {
                nameSpan.innerHTML = `<strong>${cfg.nativeName}</strong> <span style="opacity: 0.7; font-size: 0.9em;">(${cfg.fullName})</span>`;
            } else {
                nameSpan.innerHTML = `<strong>${cfg.fullName}</strong>`;
            }
            btn.appendChild(nameSpan);

            listContainer.appendChild(btn);
            buttons.push({ btn, cfg });
        });

        searchInput.oninput = (e) => {
            const query = (e.target as HTMLInputElement).value.toLowerCase();
            let visibleCount = 0;

            buttons.forEach(({ btn, cfg }) => {
                const matches = cfg.fullName.toLowerCase().includes(query) ||
                    cfg.nativeName.toLowerCase().includes(query) ||
                    cfg.id.toLowerCase().includes(query);

                btn.style.display = matches ? 'flex' : 'none';
                if (matches) visibleCount++;
            });
        };

        searchInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                const visibleBtn = buttons.find(({ btn }) => btn.style.display !== 'none');
                if (visibleBtn) {
                    this.options.changeLanguage(visibleBtn.cfg.id);
                }
            }
        });

        this.card.appendChild(listContainer);
        this.backdrop.appendChild(this.card);

        this.handleEsc = this.handleEsc.bind(this);
        document.addEventListener('keydown', this.handleEsc);

        // Focus search input on open
        setTimeout(() => searchInput.focus(), 100);
    }

    private handleEsc(e: KeyboardEvent) {
        if (e.key === 'Escape') this.options.onClose();
    }

    public getElement() {
        return this.backdrop;
    }

    public destroy() {
        document.removeEventListener('keydown', this.handleEsc);
        this.backdrop.remove();
    }
}
